<template>
  <Teleport to="body">
    <Transition name="modal-fade" :style="{ visibility: modalVisibility }">
      <div
        class="modal-wrapper" :class="{ 'no-blur': props.modal.modalState?.props?.noBlur }" aria-modal="true" role="dialog"
        tabindex="-1"
      >
        <ModalBase
          ref="modalContainer" v-bind="props?.modal.modalState?.props" @close-action="onCloseModal" @keydown.esc.stop.prevent="onCloseModal"
          @close-icon-action="invokeCloseModal"
        >
          <template v-if="props?.modal.modalState?.componentHeader" #custom-header>
            <component :is="props?.modal.modalState?.componentHeader" v-bind="props?.modal.modalState?.props" @close-action="onCloseModal" />
          </template>
          <template v-if="props?.modal.modalState?.componentContent" #custom-component>
            <component :is="props?.modal.modalState?.componentContent" v-bind="props?.modal.modalState?.props" />
          </template>
          <template v-if="props?.modal.modalState?.componentFooter" #custom-footer>
            <component :is="props?.modal.modalState?.componentFooter" v-bind="props?.modal.modalState?.props" />
          </template>
        </ModalBase>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useStore } from 'vuex'
import ModalBase from './modal-structure/ModalBase.vue'
import { computed, nextTick, onMounted, onUnmounted, provide, ref } from 'vue'
import { useEventListener } from '@vueuse/core'
import { openModalCustomConfirmation } from '@/services/utils'
import { CRUD, DataType } from '@/interfaces'
import { ModalLayer, ModalType, hideAndUnmountMainModal } from '@/components/modals/modals-util'
import { IModalState } from '@/store/custom-modal.module'
import { IOverlayModalState } from '@/store/overlay-modal.module'
import { MODAL_ACTIONS_INJECTION_KEY } from '@/constants'

const modalContainer = ref()
const store = useStore()

const props = defineProps<{
  modal: IModalState | IOverlayModalState
}>()

const globalIsOverlayModalOpen = computed(() => store.getters['overlayModal/getIsOverlayModalOpen']())

const modalVisibility = computed(() => {
  const isOverlayModalOpen = props.modal?.modalLayer === ModalLayer.Overlay && props?.modal.isOverlayModalOpen
  const isMainModalOpen = props.modal?.modalLayer === ModalLayer.Main && props?.modal.isMainModalOpen
  const isOnlyMainModalOpen = isMainModalOpen && !globalIsOverlayModalOpen.value
  // When overlay is open, main modal must be already open
  const shouldShow = isOverlayModalOpen || isOnlyMainModalOpen
  const visibility = shouldShow ? 'visible' : 'hidden'
  return visibility
})

let clickOriginTarget: EventTarget | null = null

const handleMouseDown = (e: MouseEvent) => {
  clickOriginTarget = e.target
}

const handleMouseUp = async (e: MouseEvent) => {
  const isHiddenOverlayInstance = props.modal.modalLayer === ModalLayer.Overlay && !store.state.overlayModal.isOverlayModalOpen
  if (isHiddenOverlayInstance) return

  if (props.modal.modalType === ModalType.DISCARD_CHANGES) {
    clickOriginTarget = null
    return
  }

  if (props?.modal.modalState?.props?.disableClickOutside) return

  const releaseTarget = e.target as HTMLElement
  const modalEl = modalContainer.value?.$el || modalContainer.value

  const clickStartedInsideModal = clickOriginTarget && modalEl?.contains(clickOriginTarget)
  const clickReleasedOnOverlay = releaseTarget && !modalEl?.contains(releaseTarget) && releaseTarget.className === 'modal-wrapper'

  const isClickOnOverlay = !clickStartedInsideModal && clickReleasedOnOverlay

  if (isClickOnOverlay) {
    await onCloseModal()
  }
  clickOriginTarget = null
}

useEventListener('mousedown', handleMouseDown, { capture: true })
useEventListener('mouseup', handleMouseUp, { capture: true })

function isPayloadEmpty(obj: Record<string, unknown>): boolean {
  return Object.values(obj).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0
    }
    return !value
  })
}

async function onCloseModal() {
  if (!isPayloadEmpty(store.state.modal.modalState.payload) && props.modal.modalState.props.showDiscardStep) {
    const customConfirmationProps = {
      isOverlay: true,
      isOverlayModalOpen: true,
      action: CRUD.ConfirmClose,
      type: DataType.Folder,
      onDismiss: () => {
        closeOverlayModal()
      },
      onConfirm: () => {
        closeModal()
        closeOverlayModal()
      }
    }
    openModalCustomConfirmation(customConfirmationProps, ModalType.DISCARD_CHANGES)
  } else {
    if (store.state.overlayModal.isOverlayModalOpen) {
      closeOverlayModal()
    } else {
      await closeModal()
    }
  }
}

function closeOverlayModal() {
  store.dispatch('overlayModal/closeModal')
  store.dispatch('modal/revealModal')
  focusOnFirstInputEl()
}

async function closeModal() {
  const modalProps = props.modal.modalState.props
  await modalProps.onDismiss?.({ ...modalProps })
  if (!store.state.overlayModal.isOverlayModalOpen) {
    hideAndUnmountMainModal()
  }
}

function focusOnFirstInputEl() {
  nextTick(() => {
    const modalElement = modalContainer.value?.$el
    if (modalElement) {
      const firstInput = modalElement.querySelector('input')
      if (firstInput) {
        firstInput.focus()
      }
    }
  })
}

async function invokeCloseModal() {
  const modalProps = props.modal.modalState.props
  if (typeof modalProps.onCloseModalOnIcon === 'function') {
    await modalProps.onCloseModalOnIcon({ ...modalProps })
  } else {
    await onCloseModal()
  }
}

onMounted(() => {
  // hide overflow if needed
  const body = document.body
  if (body) body.style.overflow = 'hidden'
  focusOnFirstInputEl()
})

onUnmounted(() => {
  // reset overflow to auto
  const body = document.body
  if (body) body.style.overflow = 'auto'
})

provide(MODAL_ACTIONS_INJECTION_KEY, closeModal)
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.modal-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  z-index: $zIndex999998;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.564);
  backdrop-filter: blur(3px);
  display: grid;
  place-items: center;

  &.no-blur {
    backdrop-filter: blur(0px);
    background: rgba(0, 0, 0, 0.34);
  }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: 0.25s ease all;
}
</style>
